import { computeInvoiceTotals } from './zovcurInvoiceStore';
import { normalizeSalesmanName } from './salesPerformanceUtils';

export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const getInvoiceDate = (invoice) => {
  const raw = invoice?.invoiceDetails?.billDate || invoice?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const buildInvoiceStats = (invoices = []) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  let totalRevenue = 0;
  let monthlyRevenue = 0;
  let totalStrips = 0;
  let paidCount = 0;
  let unpaidCount = 0;
  let paidAmount = 0;
  let unpaidAmount = 0;
  let thisMonthInvoices = 0;

  const monthlyMap = {};
  const salesmanMap = {};

  invoices.forEach((invoice) => {
    const totals = computeInvoiceTotals(invoice?.products || []);
    const amount = totals.roundedNetAmount;
    totalRevenue += amount;
    totalStrips += totals.quantity;

    const isPaid = (invoice.paymentStatus || 'Unpaid') === 'Paid';
    if (isPaid) {
      paidCount += 1;
      paidAmount += amount;
    } else {
      unpaidCount += 1;
      unpaidAmount += amount;
    }

    const billDate = getInvoiceDate(invoice);
    if (billDate) {
      const key = `${billDate.getFullYear()}-${String(billDate.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[key]) {
        monthlyMap[key] = { key, amount: 0, strips: 0, invoices: 0 };
      }
      monthlyMap[key].amount += amount;
      monthlyMap[key].strips += totals.quantity;
      monthlyMap[key].invoices += 1;

      if (billDate.getMonth() === currentMonth && billDate.getFullYear() === currentYear) {
        monthlyRevenue += amount;
        thisMonthInvoices += 1;
      }
    }

    const salesman = String(invoice?.invoiceDetails?.salesmanName || '').trim();
    if (salesman) {
      const key = normalizeSalesmanName(salesman);
      if (!salesmanMap[key]) {
        salesmanMap[key] = { name: salesman, amount: 0, strips: 0, invoices: 0 };
      }
      salesmanMap[key].amount += amount;
      salesmanMap[key].strips += totals.quantity;
      salesmanMap[key].invoices += 1;
    }
  });

  const monthlyTrend = Object.values(monthlyMap)
    .sort((a, b) => a.key.localeCompare(b.key))
    .slice(-6)
    .map((item) => {
      const [year, month] = item.key.split('-');
      return {
        label: `${MONTH_SHORT[Number(month) - 1]} ${String(year).slice(-2)}`,
        amount: item.amount,
        strips: item.strips,
        invoices: item.invoices
      };
    });

  const topSalesmen = Object.values(salesmanMap)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const recentInvoices = [...invoices]
    .sort((a, b) => {
      const da = getInvoiceDate(a)?.getTime() || 0;
      const db = getInvoiceDate(b)?.getTime() || 0;
      return db - da;
    })
    .slice(0, 6);

  return {
    totalInvoices: invoices.length,
    totalRevenue,
    monthlyRevenue,
    thisMonthInvoices,
    totalStrips,
    paidCount,
    unpaidCount,
    paidAmount,
    unpaidAmount,
    monthlyTrend,
    topSalesmen,
    recentInvoices,
    paymentBreakdown: [
      { name: 'Paid', value: paidCount, amount: paidAmount },
      { name: 'Unpaid', value: unpaidCount, amount: unpaidAmount }
    ]
  };
};

export const formatRelativeTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
